import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Clock, Mic, Hash, AlertCircle, MessageSquare, PhoneOff, Timer, Volume2, Music, Speaker, Waves, Smartphone, Voicemail, DollarSign, Search, Calculator, AudioLines, ListChecks } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFormContext } from "react-hook-form";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X, Plus } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { isEnglishOnly } from "../languageSetup";

export default function ConfigurationTab() {
  const { control, watch, setValue } = useFormContext();
  const [phraseInput, setPhraseInput] = useState("");
  // The Smart Filler runs on English-only assistants alone (voxi-2-0 ConvFill filler).
  const englishOnly = isEnglishOnly({
    llm_provider: watch("llm_provider"),
    llm_model: watch("llm_model"),
    language_configuration: watch("language_configuration"),
  });

  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardHeader>
        <CardTitle className="text-foreground">Configuration</CardTitle>
        <CardDescription>
          Configure additional settings for your assistant.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Accordion type="single" collapsible className="w-full space-y-4">
          {/* Agent Tools Section */}
          <AccordionItem value="agent-tools" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Agent Tools</h3>
                <p className="text-sm text-muted-foreground">
                  Control which built-in tools are available to the agent during calls.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="border rounded-md p-6 bg-background space-y-6">

                <FormField
                  control={control}
                  name="enableWaitForUser"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">Wait For User</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? false)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        When enabled, the agent can pause for 30 seconds when the user says
                        "give me a moment" or "let me think".
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                <FormField
                  control={control}
                  name="enableCallScreening"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Smartphone className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">Call Screening (Apple iOS)</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? false)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        When enabled, the agent can respond to Apple iOS Call Screening prompts
                        ("Please state your name and reason for calling").
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                <FormField
                  control={control}
                  name="enableEndCall"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <PhoneOff className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">End Call</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? true)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        When enabled, the agent can end the call when the conversation is complete.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                <FormField
                  control={control}
                  name="enableAnsweringMachine"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Voicemail className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">Answering Machine Detection</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? false)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        When enabled, the agent detects voicemail systems and leaves a message automatically.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                <FormField
                  control={control}
                  name="enableCheckCtcBudget"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">CTC Budget Check</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? false)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        When enabled, the agent can validate candidate salary expectations against the configured budget.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                <FormField
                  control={control}
                  name="enableSearchKnowledge"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Search className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">Knowledge Base Search</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? false)}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                              // Same switch as the Knowledge Base tab; keep both flags equal.
                              setValue("knowledgeBaseEnabled", checked, { shouldDirty: true });
                            }}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        When enabled, the agent can search the configured knowledge base for relevant information.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                <FormField
                  control={control}
                  name="enableLoanEligibility"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Calculator className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">Loan Eligibility Calculation</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? false)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        When enabled, the agent can calculate loan eligibility based on the caller's financial profile.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                <FormField
                  control={control}
                  name="enableStartTechnicalQuestions"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <ListChecks className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">Start Technical Questions</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? false)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        When enabled, the agent uses the managed technical-interview state machine
                        (calls the <code>start_technical_questions</code> tool to begin Phase 2 and
                        receives questions one-by-one via <code>&lt;CURRENT_QUESTION&gt;</code>).
                        Disable for custom agents that handle technical questions via their own prompt.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                <FormField
                  control={control}
                  name="enableContactForm"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <ListChecks className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">Contact Form (V2 web sessions only)</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? false)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        When enabled, the agent can show an input form in the web widget to collect
                        phone number and email instead of asking the caller to speak them aloud.
                        Only takes effect on V2 web sessions (browser widget) — a real phone call has
                        no form to show, so leave this off for phone-only assistants.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Voicemail Detection Section */}
          <AccordionItem value="voicemail" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Voicemail Detection</h3>
                <p className="text-sm text-muted-foreground">
                  Configure how the assistant detects and handles voicemail.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="border rounded-md p-6 bg-background">
                <FormField
                  control={control}
                  name="voicemailMessage"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Voicemail Message
                        </FormLabel>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Message your assistant will leave when voicemail is detected. The AI will automatically detect voicemail greetings and leave this message. If not set, the assistant will generate an appropriate message.
                      </FormDescription>
                      <FormControl>
                        <Textarea
                          placeholder="e.g., Hi, this is calling from [Company]. I'll try reaching you again later. Have a great day!"
                          className="min-h-24 max-h-[60vh] overflow-y-auto bg-muted text-foreground border-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Stop Speaking Plan Section */}
          <AccordionItem value="stop-speaking" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Stop Speaking Plan</h3>
                <p className="text-sm text-muted-foreground">
                  Configure when the assistant should stop talking.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="border rounded-md p-6 bg-background">
                <div className="space-y-6">
                  <FormField
                    control={control}
                    name="numberOfWords"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Hash className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">
                            Number of words
                          </FormLabel>
                          <div className="ml-auto text-foreground">
                            {field.value || 0}
                          </div>
                        </div>
                        <FormDescription className="text-muted-foreground mb-2">
                          This is the number of words that the customer has to say
                          before the assistant will stop talking.
                        </FormDescription>
                        <FormControl>
                          <Slider
                            min={0}
                            max={10}
                            step={1}
                            defaultValue={[field.value || 0]}
                            onValueChange={(vals) => field.onChange(vals[0])}
                            className="text-neon-purple"
                          />
                        </FormControl>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0</span>
                          <span>10</span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="voiceSeconds"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Mic className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">
                            Voice seconds
                          </FormLabel>
                          <div className="ml-auto text-foreground">
                            {field.value || 0.2}s
                          </div>
                        </div>
                        <FormDescription className="text-muted-foreground mb-2">
                          This is the seconds customer has to speak before the
                          assistant stops talking.
                        </FormDescription>
                        <FormControl>
                          <Slider
                            min={0}
                            max={0.5}
                            step={0.1}
                            defaultValue={[field.value || 0.2]}
                            onValueChange={(vals) => field.onChange(vals[0])}
                            className="text-neon-purple"
                          />
                        </FormControl>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0 (sec)</span>
                          <span>0.5 (sec)</span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="backOffSeconds"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">
                            Back off seconds
                          </FormLabel>
                          <div className="ml-auto text-foreground">
                            {field.value || 1}s
                          </div>
                        </div>
                        <FormDescription className="text-muted-foreground mb-2">
                          This is the seconds to wait before the assistant will
                          start talking again after being interrupted.
                        </FormDescription>
                        <FormControl>
                          <Slider
                            min={0}
                            max={10}
                            step={0.5}
                            defaultValue={[field.value || 1]}
                            onValueChange={(vals) => field.onChange(vals[0])}
                            className="text-neon-purple"
                          />
                        </FormControl>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0 (sec)</span>
                          <span>10 (sec)</span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="minInterruptionDuration"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Timer className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">
                            Min Interruption Duration
                          </FormLabel>
                          <div className="ml-auto text-foreground">
                            {field.value || 0.2}s
                          </div>
                        </div>
                        <FormDescription className="text-muted-foreground mb-2">
                          Minimum duration of user speech before it counts as an interruption.
                        </FormDescription>
                        <FormControl>
                          <Slider
                            min={0.1}
                            max={2.0}
                            step={0.1}
                            defaultValue={[field.value || 0.2]}
                            onValueChange={(vals) => field.onChange(vals[0])}
                            className="text-neon-purple"
                          />
                        </FormControl>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0.1s</span>
                          <span>2.0s</span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="minEndpointingDelay"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Timer className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">
                            Min Endpointing Delay
                          </FormLabel>
                          <div className="ml-auto text-foreground">
                            {field.value || 0.5}s
                          </div>
                        </div>
                        <FormDescription className="text-muted-foreground mb-2">
                          Minimum delay before the assistant starts responding after detecting the end of speech.
                        </FormDescription>
                        <FormControl>
                          <Slider
                            min={0}
                            max={10}
                            step={0.1}
                            defaultValue={[field.value || 0.5]}
                            onValueChange={(vals) => field.onChange(vals[0])}
                            className="text-neon-purple"
                          />
                        </FormControl>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0s (immediate)</span>
                          <span>10s</span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="maxEndpointingDelay"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Timer className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">
                            Max Endpointing Delay
                          </FormLabel>
                          <div className="ml-auto text-foreground">
                            {field.value || 5.0}s
                          </div>
                        </div>
                        <FormDescription className="text-muted-foreground mb-2">
                          Maximum delay before the assistant must respond, regardless of speech detection status.
                        </FormDescription>
                        <FormControl>
                          <Slider
                            min={0.1}
                            max={15}
                            step={0.1}
                            defaultValue={[field.value || 5.0]}
                            onValueChange={(vals) => field.onChange(vals[0])}
                            className="text-neon-purple"
                          />
                        </FormControl>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0.1s</span>
                          <span>15s (patient)</span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="turnDetectionEnabled"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Mic className="h-5 w-5 text-muted-foreground" />
                            <FormLabel className="text-foreground">
                              Turn Detection
                            </FormLabel>
                          </div>
                          <FormControl>
                            <Switch
                              checked={Boolean(field.value)}
                              onCheckedChange={(checked) => {
                                console.log("Turn detection toggle clicked:", checked);
                                field.onChange(checked);
                              }}
                              disabled={false}
                            />
                          </FormControl>
                        </div>
                        <FormDescription className="text-muted-foreground mb-2">
                          Enable or disable AI-powered turn detection for conversation flow.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="turnDetectionThreshold"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Timer className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">
                            Turn Detection Threshold
                          </FormLabel>
                          <div className="ml-auto text-foreground">
                            {field.value !== undefined ? field.value : 'Default'}
                          </div>
                        </div>
                        <FormDescription className="text-muted-foreground mb-2">
                          Custom threshold for turn detection sensitivity. Leave undefined to use the optimized default for English.
                        </FormDescription>
                        <FormControl>
                          <Slider
                            min={0.1}
                            max={1.0}
                            step={0.05}
                            defaultValue={[field.value || 0.5]}
                            onValueChange={(vals) => field.onChange(vals[0])}
                            className="text-neon-purple"
                            disabled={field.value === undefined || !Boolean(watch("turnDetectionEnabled"))}
                          />
                        </FormControl>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0.1 (sensitive)</span>
                          <span>1.0 (conservative)</span>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            className="text-xs text-foreground hover:text-foreground disabled:text-ink-faint"
                            onClick={() => field.onChange(undefined)}
                            disabled={!Boolean(watch("turnDetectionEnabled"))}
                          >
                            Use Default
                          </button>
                          <button
                            type="button"
                            className="text-xs text-foreground hover:text-foreground disabled:text-ink-faint"
                            onClick={() => field.onChange(0.5)}
                            disabled={!Boolean(watch("turnDetectionEnabled"))}
                          >
                            Set Custom
                          </button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="preemptiveGeneration"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Mic className="h-5 w-5 text-muted-foreground" />
                            <FormLabel className="text-foreground">
                              Preemptive Generation
                            </FormLabel>
                          </div>
                          <FormControl>
                            <Switch
                              checked={Boolean(field.value)}
                              onCheckedChange={(checked) => {
                                console.log("Preemptive generation toggle clicked:", checked);
                                field.onChange(checked);
                              }}
                              disabled={false}
                            />
                          </FormControl>
                        </div>
                        <FormDescription className="text-muted-foreground mb-2">
                          Enable preemptive generation to start generating responses before user finishes speaking for faster response times.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Greeting Delay Section (voxi-2-0 issue #221) */}
          <AccordionItem value="greeting-delay" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Greeting Delay</h3>
                <p className="text-sm text-muted-foreground">
                  How long the agent waits for the caller to say hello before it greets them.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="border rounded-md p-6 bg-background space-y-6">
                <FormField
                  control={control}
                  name="greeting_wait_ms"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Timer className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Greeting Delay (ms)
                        </FormLabel>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        On outbound phone calls the greeting starts when the caller says hello, or
                        after this many milliseconds, whichever comes first. 0 greets at once.
                        Leave empty to use the server default (3000). Maximum 3000.
                      </FormDescription>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          max="3000"
                          step="100"
                          placeholder="Server default (3000)"
                          className="bg-muted text-foreground border-input"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Smart Filler Section (voxi-2-0 ConvFill filler, configuration.convfill_enabled) */}
          <AccordionItem value="smart-filler" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Smart Filler</h3>
                <p className="text-sm text-muted-foreground">
                  Say a short line like "Mm, okay." while the reply is being prepared.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="border rounded-md p-6 bg-background space-y-6">
                <FormField
                  control={control}
                  name="convfill_enabled"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">Enable Smart Filler</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={englishOnly && Boolean(field.value)}
                            onCheckedChange={field.onChange}
                            disabled={!englishOnly}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground">
                        A small model says a few words, or nothing, while the LLM prepares the
                        reply, and the reply follows word for word. Cuts the pause after the caller
                        stops speaking. V2 phone calls only, on servers that have the filler set up.
                      </FormDescription>
                      {!englishOnly && (
                        <p className="text-sm text-muted-foreground">
                          Only for English. Set Language Configuration to English alone to turn this
                          on. Not available with a realtime model.
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Idle Configuration Section */}
          <AccordionItem value="idle-config" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Idle Configuration</h3>
                <p className="text-sm text-muted-foreground">
                  Configure assistant behavior during idle periods.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="border rounded-md p-6 bg-background space-y-6">
                <FormField
                  control={control}
                  name="idleTime"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Timer className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Idle Time (seconds)
                        </FormLabel>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Duration in seconds before the system enters an idle state.
                      </FormDescription>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          placeholder="e.g., 10"
                          className="bg-muted text-foreground border-input"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="idleRepeatCount"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Hash className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Idle Repeat Count
                        </FormLabel>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Number of times the idle message should be repeated.
                      </FormDescription>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          placeholder="e.g., 3"
                          className="bg-muted text-foreground border-input"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="idleMessage"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Idle Message
                        </FormLabel>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Message to be displayed during the idle state.
                      </FormDescription>
                      <FormControl>
                        <Textarea
                          placeholder="e.g., Are you still there?"
                          className="min-h-24 max-h-[60vh] overflow-y-auto bg-muted text-foreground border-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="idleHangUpMessage"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <PhoneOff className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Idle Hang Up Message
                        </FormLabel>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Message to be displayed before hanging up due to inactivity.
                      </FormDescription>
                      <FormControl>
                        <Textarea
                          placeholder="e.g., It seems you're no longer available. Goodbye."
                          className="min-h-24 max-h-[60vh] overflow-y-auto bg-muted text-foreground border-input"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>


          {/* Background Audio Configuration Section */}
          <AccordionItem value="background-audio" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Background Audio</h3>
                <p className="text-sm text-muted-foreground">
                  Configure ambient and thinking sounds during calls.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="border rounded-md p-6 bg-background space-y-6">

                {/* Enable/Disable Background Audio */}
                <FormField
                  control={control}
                  name="background_audio_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <Speaker className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-base">Enable Background Audio</FormLabel>
                        </div>
                        <FormDescription>
                          Play ambient and thinking sounds during calls to create a more natural experience.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value ?? true}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Ambient Sound Type */}
                <FormField
                  control={control}
                  name="ambient_sound_type"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Music className="h-5 w-5 text-muted-foreground" />
                        <FormLabel>Ambient Sound Type</FormLabel>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Background sound that plays continuously during the call.
                      </FormDescription>
                      <Select
                        disabled={!watch("background_audio_enabled")}
                        value={field.value || "crowded_room"}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select ambient sound" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="crowded_room">Crowded Room</SelectItem>
                          <SelectItem value="office_ambience">Office Ambience</SelectItem>
                          <SelectItem value="none">None</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Ambient Sound Volume */}
                <FormField
                  control={control}
                  name="ambient_sound_volume"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Volume2 className="h-5 w-5 text-muted-foreground" />
                        <FormLabel>Ambient Sound Volume</FormLabel>
                        <div className="ml-auto text-foreground">
                          {((field.value ?? 0.1) * 100).toFixed(0)}%
                        </div>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Volume level for the ambient background sound.
                      </FormDescription>
                      <FormControl>
                        <Slider
                          disabled={!watch("background_audio_enabled") || watch("ambient_sound_type") === "none"}
                          min={0}
                          max={1}
                          step={0.05}
                          defaultValue={[field.value ?? 0.1]}
                          onValueChange={(vals) => field.onChange(vals[0])}
                          className="text-neon-purple"
                        />
                      </FormControl>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0%</span>
                        <span>100%</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Thinking Sound Type */}
                <FormField
                  control={control}
                  name="thinking_sound_type"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Music className="h-5 w-5 text-muted-foreground" />
                        <FormLabel>Thinking Sound Type</FormLabel>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Sound that plays when the assistant is processing or thinking.
                      </FormDescription>
                      <Select
                        disabled={!watch("background_audio_enabled")}
                        value={field.value || "office_ambience"}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select thinking sound" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="office_ambience">Office Ambience</SelectItem>
                          <SelectItem value="crowded_room">Crowded Room</SelectItem>
                          <SelectItem value="none">None</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Thinking Sound Volume */}
                <FormField
                  control={control}
                  name="thinking_sound_volume"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Volume2 className="h-5 w-5 text-muted-foreground" />
                        <FormLabel>Thinking Sound Volume</FormLabel>
                        <div className="ml-auto text-foreground">
                          {((field.value ?? 1.0) * 100).toFixed(0)}%
                        </div>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Volume level for the thinking sound.
                      </FormDescription>
                      <FormControl>
                        <Slider
                          disabled={!watch("background_audio_enabled") || watch("thinking_sound_type") === "none"}
                          min={0}
                          max={1}
                          step={0.05}
                          defaultValue={[field.value ?? 1.0]}
                          onValueChange={(vals) => field.onChange(vals[0])}
                          className="text-neon-purple"
                        />
                      </FormControl>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0%</span>
                        <span>100%</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* VAD Configuration Section */}
          <AccordionItem value="vad-config" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Voice Activity Detection (VAD)</h3>
                <p className="text-sm text-muted-foreground">
                  Configure how the assistant detects when users are speaking.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">


              <div className="border rounded-md p-6 bg-background space-y-6">
                <FormField
                  control={control}
                  name="vadMinSpeechDuration"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Volume2 className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Min Speech Duration
                        </FormLabel>
                        <div className="ml-auto text-foreground">
                          {field.value || 0.25}s
                        </div>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Minimum duration of speech before considering it as voice activity.
                      </FormDescription>
                      <FormControl>
                        <Slider
                          min={0.05}
                          max={1.0}
                          step={0.05}
                          defaultValue={[field.value || 0.25]}
                          onValueChange={(vals) => field.onChange(vals[0])}
                          className="text-neon-purple"
                        />
                      </FormControl>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0.05s</span>
                        <span>1.0s</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="vadMinSilenceDuration"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Timer className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Min Silence Duration
                        </FormLabel>
                        <div className="ml-auto text-foreground">
                          {field.value || 0.4}s
                        </div>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Minimum duration of silence before considering speech as ended.
                      </FormDescription>
                      <FormControl>
                        <Slider
                          min={0.1}
                          max={2.0}
                          step={0.1}
                          defaultValue={[field.value || 0.4]}
                          onValueChange={(vals) => field.onChange(vals[0])}
                          className="text-neon-purple"
                        />
                      </FormControl>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0.1s</span>
                        <span>2.0s</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="vadPrefixPaddingDuration"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Prefix Padding Duration
                        </FormLabel>
                        <div className="ml-auto text-foreground">
                          {field.value || 0.0}s
                        </div>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Additional audio to include before detected speech starts.
                      </FormDescription>
                      <FormControl>
                        <Slider
                          min={0.0}
                          max={1.0}
                          step={0.1}
                          defaultValue={[field.value || 0.0]}
                          onValueChange={(vals) => field.onChange(vals[0])}
                          className="text-neon-purple"
                        />
                      </FormControl>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0.0s</span>
                        <span>1.0s</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="vadActivationThreshold"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Mic className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          Activation Threshold
                        </FormLabel>
                        <div className="ml-auto text-foreground">
                          {field.value || 0.7}
                        </div>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Sensitivity threshold for voice activity detection (0.0 = very sensitive, 1.0 = less sensitive).
                      </FormDescription>
                      <FormControl>
                        <Slider
                          min={0.1}
                          max={1.0}
                          step={0.1}
                          defaultValue={[field.value || 0.7]}
                          onValueChange={(vals) => field.onChange(vals[0])}
                          className="text-neon-purple"
                        />
                      </FormControl>
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>0.1 (sensitive)</span>
                        <span>1.0 (less sensitive)</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Background Voice Cancellation Section */}
          <AccordionItem value="bvc-config" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Background Voice Cancellation</h3>
                <p className="text-sm text-muted-foreground">
                  Suppress background voices and improve audio quality for calls.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="border rounded-md p-6 bg-background space-y-6">
                <FormField
                  control={control}
                  name="enableBVC"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Waves className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">
                            Enable BVC
                          </FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? true)}
                            onCheckedChange={(checked) => {
                              console.log("BVC toggle clicked:", checked);
                              field.onChange(checked);
                            }}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Enable AI-powered background voice cancellation to suppress background speakers and improve audio clarity.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="bvcMode"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2 mb-2">
                        <Volume2 className="h-5 w-5 text-muted-foreground" />
                        <FormLabel className="text-foreground">
                          BVC Mode
                        </FormLabel>
                      </div>
                      <FormDescription className="text-muted-foreground mb-2">
                        Select the noise cancellation mode. "Telephony" is optimized for phone calls (recommended for SIP), while "Standard" works for general use.
                      </FormDescription>
                      <FormControl>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="telephony"
                              checked={field.value === 'telephony' || !field.value}
                              onChange={() => field.onChange('telephony')}
                              className="text-neon-purple"
                              disabled={!Boolean(watch("enableBVC") ?? true)}
                            />
                            <span className={!Boolean(watch("enableBVC") ?? true) ? "text-muted-foreground" : ""}>
                              Telephony (Recommended)
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="standard"
                              checked={field.value === 'standard'}
                              onChange={() => field.onChange('standard')}
                              className="text-neon-purple"
                              disabled={!Boolean(watch("enableBVC") ?? true)}
                            />
                            <span className={!Boolean(watch("enableBVC") ?? true) ? "text-muted-foreground" : ""}>
                              Standard
                            </span>
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Filler Sentences Section */}
          <AccordionItem value="filler-config" className="border rounded-lg bg-card shadow-card">
            <AccordionTrigger className="px-4">
              <div className="flex flex-col items-start">
                <h3 className="text-lg font-semibold">Filler Sentences</h3>
                <p className="text-sm text-muted-foreground">
                  Play instant acknowledgment phrases while the AI thinks to reduce perceived silence.
                </p>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="border rounded-md p-6 bg-background space-y-6">
                {/* Enable toggle */}
                <FormField
                  control={control}
                  name="fillerEnabled"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <AudioLines className="h-5 w-5 text-muted-foreground" />
                          <FormLabel className="text-foreground">Enable Filler Sentences</FormLabel>
                        </div>
                        <FormControl>
                          <Switch
                            checked={Boolean(field.value ?? true)}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </div>
                      <FormDescription className="text-muted-foreground">
                        When enabled, the assistant plays a short pre-synthesized phrase immediately after the user finishes speaking, masking LLM response latency.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Custom phrases */}
                {watch("fillerEnabled") !== false && (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">Custom Phrases</p>
                      <p className="text-xs text-muted-foreground">
                        Add your own phrases. Leave empty to use the built-in defaults ("Got it.", "Sure.", etc.).
                      </p>
                    </div>

                    {/* Tag display */}
                    {(watch("fillerPhrases") as string[] ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {(watch("fillerPhrases") as string[]).map((phrase: string) => (
                          <Badge
                            key={phrase}
                            variant="secondary"
                            className="flex items-center gap-1 pr-1 text-sm"
                          >
                            {phrase}
                            <button
                              type="button"
                              onClick={() =>
                                setValue(
                                  "fillerPhrases",
                                  (watch("fillerPhrases") as string[]).filter((p: string) => p !== phrase),
                                  { shouldDirty: true }
                                )
                              }
                              className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Add input */}
                    <div className="flex gap-2">
                      <Input
                        placeholder='e.g. "Got it." or "Sure, one moment."'
                        value={phraseInput}
                        onChange={(e) => setPhraseInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const trimmed = phraseInput.trim();
                            const current = (watch("fillerPhrases") as string[]) ?? [];
                            if (trimmed && !current.includes(trimmed)) {
                              setValue("fillerPhrases", [...current, trimmed], { shouldDirty: true });
                            }
                            setPhraseInput("");
                          }
                        }}
                        className="bg-muted text-foreground border-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const trimmed = phraseInput.trim();
                          const current = (watch("fillerPhrases") as string[]) ?? [];
                          if (trimmed && !current.includes(trimmed)) {
                            setValue("fillerPhrases", [...current, trimmed], { shouldDirty: true });
                          }
                          setPhraseInput("");
                        }}
                      >
                        <Plus className="h-4 w-4" />
                        Add
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}
